import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  useDataModel,
  extend,
  arrayOf,
  namedFragment,
  customType,
  bool as boolType,
  shape,
  string as stringType,
  alias, params, typename,
} from '@thd-nucleus/data-sources';
import { useBreakpoint, ThemeProvider } from '@one-thd/sui-atomic-components';
import { ComponentSelector } from '@thd-olt-component-react/component-selector';
import { getThemeByName } from '@thd-olt-component-react/stencil-themes';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { Spotlight } from './Spotlight';
import { SpotlightContainerRenderer } from './SpotlightContainerRenderer';
import { SpotlightSelector } from './personalization/SpotlightSelector';
import { normalizeSpotlightContainerData } from './utils/normalizeSpotlightContainerData';
import { SpotlightRenderer } from './SpotlightRenderer';

const SpotlightContainer = ({
  componentId = '',
  slotContentId: slotName = '',
  componentClass = '',
  componentPosition = 1,
  showDescription = false,
  useB2bStyles = false,
  fontWeight = 'display',
  cmMmcParam = '',
  renderDefault = false,
  variationIds = [],
  isSeoBot = false,
  maxSlots = undefined,
  overflowBehavior: overflowBehaviorProp = 'Wrap',
  extraCardOnLargeAndAbove: extraCardOnLargeAndAboveProp = false,
  mediaCollectionIndex = 0,
  proAnalyticsData: proAnalyticsDataProp = {}
}) => {
  const { customer, channel } = useContext(ExperienceContext);
  const thdCustomer = typeof window !== 'undefined' ? window.THDCustomer?.default : null;

  const proAnalyticsData = Object.keys(proAnalyticsDataProp).length > 0
    ? proAnalyticsDataProp
    : {
      isB2B: customer?.type === 'b2b',
      templates: thdCustomer?.segment,
    };
  const { current: currentBreakpoint } = useBreakpoint(null, { current: true });
  const isMobile = channel === 'mobile';
  const resolvedSlotName = slotName || '';
  const useMediaContentMode = !!resolvedSlotName;
  // Fetch data from both potential sources. The hooks will skip if their conditions aren't met.
  const { data: componentData } = useDataModel('specializedContainer', {
    variables: { id: componentId, componentClass },
    skip: useMediaContentMode || slotName || !componentId || !componentClass,
  });

  const { data: slotResp, loading: mediaContentLoading, ...rest } = useDataModel('slot', {
    variables: { id: resolvedSlotName },
    fetchPolicy: 'cache-only',
    skip: !useMediaContentMode,
  });

  // Normalize the data from whichever source is active
  const normalizedData = normalizeSpotlightContainerData({
    slotResp,
    slotName: resolvedSlotName,
    componentData: useMediaContentMode ? undefined : componentData,
    maxSlots,
    overflowBehavior: overflowBehaviorProp,
    extraCardOnLargeAndAbove: extraCardOnLargeAndAboveProp,
    mediaCollectionIndex,
  });

  if (useMediaContentMode && mediaContentLoading) {
    return null;
  }

  if (!normalizedData) {
    return null;
  }

  const {
    id: containerId,
    containerTitle,
    spotlightList = [],
    overflowBehavior,
    extraCardOnLargeAndAbove,
    theme,
    itemTheme,
    title,
  } = normalizedData;

  const normalizedTitle = containerTitle || title;

  const cardCount = spotlightList.length;

  const spotlightOrientation = (
    (cardCount === 1 && !isMobile && currentBreakpoint === 'unprefixed')
    || (cardCount === 1 && currentBreakpoint !== 'sm' && currentBreakpoint !== 'unprefixed')
  ) ? 'horizontal' : 'vertical';

  const baseTheme = getThemeByName('default');
  const processedTheme = theme ? getThemeByName(theme) : baseTheme;
  const processedItemTheme = itemTheme ? getThemeByName(itemTheme) : processedTheme;
  const spotlightComponents = spotlightList.map((spotlight, index) => {
    const isSpotlight = spotlight?.__typename === 'Spotlight';
    const isSelector = spotlight?.__typename === 'ComponentSelector';
    const linkList = spotlight.linkList || [];
    const proAnalyticsCampaign = spotlight.proAnalyticsCampaign;
    const proAnalyticsComponent = spotlight.proAnalyticsComponent;

    if (isSpotlight) {
      return (
        slotResp ? (
          <SpotlightRenderer
            key={`${spotlight.id}-${index}`}
            title={spotlight.title}
            description={spotlight.description}
            link={spotlight.link}
            cta={spotlight.cta}
            previewImage={spotlight.previewImage}
            videoUrl={spotlight.videoUrl}
            richText={spotlight.richText}
            slotEntryId={spotlight.id}
            componentId={spotlight.id}
            componentClass="Spotlight"
            componentPosition={index + 1}
            showDescription={showDescription}
            useB2bStyles={useB2bStyles}
            orientation={spotlightOrientation}
            cmMmcParam={cmMmcParam}
            proAnalyticsCampaign={proAnalyticsCampaign}
            proAnalyticsComponent={proAnalyticsComponent}
            listOfLinks={linkList}
            proAnalyticsData={proAnalyticsData}
            isSlotData
          />
        ) : (
          <ImpressionProvider
            key={`${spotlight.id}-${index}`}
            data={{
              id: spotlight.id,
              name: 'Spotlight',
              component: 'Spotlight',
              position: index + 1,
            }}
          >
            <Spotlight
              key={`${spotlight.id}-${index}`}
              componentId={spotlight.id}
              componentClass="Spotlight"
              componentPosition={index + 1}
              showDescription={showDescription}
              useB2bStyles={useB2bStyles}
              orientation={spotlightOrientation}
              cmMmcParam={cmMmcParam}
              proAnalyticsData={proAnalyticsData}
            />
          </ImpressionProvider>
        ));
    }
    if (isSelector) {
      return (
        <ComponentSelector
          componentMap={{ Spotlight }}
          key={`${spotlight.id}-${index}`}
          componentId={spotlight.id}
          componentClass="ComponentSelector"
          customPropMap={{
            Spotlight: {
              useB2bStyles,
              showDescription,
              orientation: spotlightOrientation,
              componentPosition: index + 1,
              cmMmcParam,
            },
          }}
          isInCarousel={overflowBehavior === 'Carousel'}
          defaultComponent={spotlight.defaultComponent}
          variations={spotlight.variations}
          variationIds={variationIds}
          renderDefault={renderDefault}
          isSeoBot={isSeoBot}
          targetingType={spotlight.targetingType}
        />
      );
    }
    return <React.Fragment key={`empty-${index}`} />;
  }).map((spotlight, index) => (
    <ThemeProvider key={`${spotlight.key}-${index}`} theme={processedItemTheme}>
      {spotlight}
    </ThemeProvider>
  ));

  return (
    <ThemeProvider theme={processedTheme}>
      <SpotlightContainerRenderer
        componentId={containerId}
        slotName={resolvedSlotName}
        entryId={normalizedData.entryId}
        componentPosition={componentPosition}
        title={normalizedTitle}
        overflowBehavior={overflowBehavior}
        extraCardOnLargeAndAbove={extraCardOnLargeAndAbove}
        cardCount={cardCount}
        spotlightComponents={spotlightComponents}
        fontWeight={fontWeight}
      />
    </ThemeProvider>
  );
};

SpotlightContainer.displayName = 'SpotlightContainer';
SpotlightContainer.propTypes = {
  slotContentId: PropTypes.string,
  componentId: PropTypes.string,
  componentClass: PropTypes.string,
  componentPosition: PropTypes.number,
  showDescription: PropTypes.bool,
  useB2bStyles: PropTypes.bool,
  fontWeight: PropTypes.string,
  cmMmcParam: PropTypes.string,
  renderDefault: PropTypes.bool,
  isSeoBot: PropTypes.bool,
  maxSlots: PropTypes.number,
  mediaCollectionIndex: PropTypes.number,
  overflowBehavior: PropTypes.string,
  extraCardOnLargeAndAbove: PropTypes.bool,
  variationIds: PropTypes.arrayOf(
    PropTypes.shape({
      provider: PropTypes.string.isRequired,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      mbox: PropTypes.string,
      componentID: PropTypes.string,
      uniqueID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      slotNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      variation: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  proAnalyticsData: PropTypes.shape({
    isB2B: PropTypes.bool,
    templates: PropTypes.string,
  }),
};

SpotlightContainer.dataModel = extend({
  specializedContainer: alias('component').params({
    id: stringType()
      .isRequired(),
    componentClass: customType('ComponentClass')
      .enum(['SpotlightContainer'])
      .isRequired(),
  }).shape({
    SpotlightContainer: namedFragment({
      inline: true,
      fragmentType: 'SpotlightContainer'
    }).shape({
      id: stringType().isRequired(),
      title: stringType(),
      componentName: stringType(),
      overflowBehavior: stringType(),
      extraCardOnLargeAndAbove: boolType(),
      theme: stringType(),
      itemTheme: stringType(),
      spotlightList: arrayOf(shape({
        ...SpotlightSelector.dataModel.component,
        ...Spotlight.dataModel.component,
      })),
    }),
  }),
  slot: params({
    id: stringType().isRequired(),
  }).shape({
    id: stringType(),
    entryId: stringType(),
    mediaCollections: arrayOf(shape({
      title: stringType(),
      customProps: stringType(),
      media: arrayOf(shape({
        entryId: stringType(),
        title: stringType(),
        description: stringType(),
        richText: stringType(),
        callToAction: stringType(),
        customProps: stringType(),
        sourceDesktop: arrayOf(shape({
          type: stringType(),
          uri: stringType(),
          altText: stringType(),
          hyperlink: stringType(),
          customProps: stringType()
        })),
        sourceMobile: arrayOf(shape({
          type: stringType(),
          uri: stringType(),
          hyperlink: stringType(),
          altText: stringType(),
          customProps: stringType()
        })),
      })),
    })),
    __typename: typename('newSlot'),
  }),
}, Spotlight);

export { SpotlightContainer };
